import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { ChannelBadge } from '~/components/badge'
import { AppLoader } from '~/components/loader'
import { ChannelsCommandList, ScorecardsCommandList } from '~/features/command-lists'
import { GoalsForm } from '~/features/goals-form'
import { ScorecardMutators } from '~/mutators/scorecards'
import { WorkspaceMutators } from '~/mutators/workspaces'
import { useTRPC } from '~/services/trpc'
import type { ChannelType } from '~/stores/stores.types'
import { type TitleSegment, useSettingTitle } from '~/templates/settings'

import {
  AlertDialog,
  AlertDialogTrigger,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading2,
  MagicText,
} from '@hap/components'

export const Route = createFileRoute(
  '/_authenticated/$alias/settings/workspaces/$workspaceId',
)({
  component: RouteComponent,
})

function RouteComponent(): JSX.Element {
  const trpc = useTRPC()
  const { workspaceId } = Route.useParams()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [assignScorecardOpen, setAssignScorecardOpen] = useState(false)

  const { data: workspace, isLoading } = useQuery(
    trpc.workspaces.getById.queryOptions({
      workspaceId,
      with: { scorecards: true },
    }),
  )

  const { data: channels, isLoading: channelsLoading } = useQuery(
    trpc.workspaces.getChannels.queryOptions({
      workspaceId,
    }),
  )

  const { data: org } = useQuery(trpc.organizations.getThresholds.queryOptions())

  const { update } = WorkspaceMutators.useUpdate()
  const { delete: deleteWorkspace } = WorkspaceMutators.useDelete({
    onSuccess: () => {
      navigate({ to: '/$alias/settings/workspaces', search: location.search })
    },
  })
  const { assignChannels } = WorkspaceMutators.useAssignChannels()
  const { removeChannels, isPending: isRemoving } = WorkspaceMutators.useRemoveChannels()
  const { setScorecards } = WorkspaceMutators.useSetScorecards()
  const { create, isPending: isCreatingScorecard } = ScorecardMutators.useCreate({
    onSuccess: (scorecardId) => {
      navigate({
        to: `/$alias/settings/scorecards/${scorecardId}/criteria`,
        search: location.search,
      })
    },
  })

  const navigateToWorkspaces = useCallback(() => {
    navigate({ to: '/$alias/settings/workspaces', search: location.search })
  }, [location.search, navigate])

  const navigateToScorecard = useCallback(
    (scorecardId: string) => {
      navigate({
        to: `/$alias/settings/scorecards/${scorecardId}/criteria`,
        search: location.search,
      })
    },
    [location.search, navigate],
  )

  const titleSegments: TitleSegment[] = useMemo(
    () =>
      [
        { label: 'Workspaces', onClick: navigateToWorkspaces },
        workspace?.name || 'Workspace',
      ].filter(Boolean) as TitleSegment[],
    [navigateToWorkspaces, workspace?.name],
  )

  useSettingTitle(titleSegments)

  const handleNameUpdate = useCallback(
    (name: string) => {
      update({ workspaceId, values: { name } })
    },
    [workspaceId, update],
  )

  const handleGoalsUpdate = useCallback(
    async (thresholds: Record<string, unknown>) => {
      await update({ workspaceId, values: { thresholds } })
    },
    [workspaceId, update],
  )

  const handleAssignChannel = useCallback(
    async (channelId: string) => {
      await assignChannels({ workspaceId, channelIds: [channelId] })
    },
    [workspaceId, assignChannels],
  )

  const handleRemoveChannel = useCallback(
    async (channelId: string) => {
      await removeChannels({ workspaceId, channelIds: [channelId] })
    },
    [workspaceId, removeChannels],
  )

  const handleAssignScorecard = useCallback(
    (scorecardId: string) => {
      setAssignScorecardOpen(false)

      void setScorecards({
        workspaceId,
        add: [scorecardId],
        remove: [],
      })
    },
    [workspaceId, setScorecards],
  )

  const handleCreateScorecard = useCallback(
    async (name: string) => {
      const value = name.trim()
      if (!value) return

      try {
        setAssignScorecardOpen(false)
        await create({ name: value, workspaceId })
      } catch {
        // handled in mutator
      }
    },
    [workspaceId, create],
  )

  const handleRemoveScorecard = useCallback(
    (scorecardId: string) => {
      void setScorecards({
        workspaceId,
        add: [],
        remove: [scorecardId],
      })
    },
    [workspaceId, setScorecards],
  )

  const handleDelete = useCallback(async () => {
    await deleteWorkspace(workspaceId)
  }, [workspaceId, deleteWorkspace])

  if (isLoading) {
    return <AppLoader />
  }

  if (!workspace) {
    return (
      <div className="gutters w-full space-y-4 overflow-y-auto py-8">
        <div className="text-muted-foreground py-12 text-center">
          Workspace not found.
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-0 shrink grow overflow-y-auto">
      <div className="gutters mx-auto w-full max-w-[800px]">
        <header className="flex items-start py-6">
          <hgroup className="shrink grow">
            <h1 className="text-2xl font-bold">
              <MagicText
                initialValue={workspace.name}
                placeholder="Workspace name"
                id="workspace-title"
                onCommit={handleNameUpdate}
              />
            </h1>
            <p className="text-muted-foreground">Manage workspace settings</p>
          </hgroup>
          <div className="mt-1 flex shrink-0 items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" icon="dots-three-outline" size="icon" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  icon="pencil-simple"
                  onClick={() => {
                    setTimeout(() => {
                      document.getElementById('workspace-title')?.click()
                    }, 250)
                  }}
                >
                  Rename...
                </DropdownMenuItem>
                <DropdownMenuItem
                  icon="trash"
                  onClick={() => setDeleteDialogOpen(true)}
                  className="text-destructive"
                >
                  Delete workspace...
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <section className="space-y-4 py-6">
          <hgroup className="flex items-center justify-between">
            <Heading2>Channels</Heading2>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm">
                  Assign channel
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="p-0" align="end">
                <ChannelsCommandList
                  excludeIds={channels?.map((c: { id: string }) => c.id)}
                  onSelect={(channelIds) => {
                    const channelId = channelIds[0]
                    if (channelId) handleAssignChannel(channelId)
                  }}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </hgroup>
          <p className="text-muted-foreground text-sm">
            Channels assigned to this workspace will use its scorecards and goals.
          </p>

          {channelsLoading ? (
            <div className="text-muted-foreground py-4 text-center text-sm">
              Loading channels...
            </div>
          ) : channels && channels.length > 0 ? (
            <div className="space-y-1">
              {channels.map((channel: { id: string; name: string; type: string }) => (
                <div
                  key={channel.id}
                  className="flex items-center gap-2 rounded-md border px-3 py-2"
                >
                  <div className="shrink-0">
                    <ChannelBadge type={channel.type as ChannelType} />
                  </div>
                  <div className="grow">
                    <h3 className="font-medium">{channel.name}</h3>
                  </div>
                  <div className="shrink-0">
                    <AlertDialog
                      action={{
                        value: 'Unassign',
                        variant: 'destructive',
                        onClick: () => handleRemoveChannel(channel.id),
                      }}
                      title="Unassign Channel"
                      description="Unassign this channel from the workspace. This does not delete the channel."
                    >
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          icon="x"
                          disabled={isRemoving}
                        />
                      </AlertDialogTrigger>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground rounded-md border px-4 py-8 text-center text-sm">
              No channels assigned to this workspace.
            </div>
          )}
        </section>

        <section className="space-y-4 border-t py-6">
          <hgroup className="flex items-center justify-between">
            <Heading2>Scorecards</Heading2>
            <DropdownMenu
              open={assignScorecardOpen}
              onOpenChange={setAssignScorecardOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" disabled={isCreatingScorecard}>
                  Add scorecard
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="p-0" align="end">
                <ScorecardsCommandList
                  excludeIds={workspace.scorecards?.map(
                    (scorecard: { id: string }) => scorecard.id,
                  )}
                  onSelect={(scorecardIds) => {
                    const scorecardId = scorecardIds[0]
                    if (scorecardId) handleAssignScorecard(scorecardId)
                  }}
                  onCreate={handleCreateScorecard}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </hgroup>
          <p className="text-muted-foreground text-sm">
            Scorecards in this workspace are used for quality assurance evaluations.
          </p>

          {workspace.scorecards && workspace.scorecards.length > 0 ? (
            <div className="space-y-1">
              {workspace.scorecards.map((scorecard) => (
                <div
                  key={scorecard.id}
                  className="flex items-center gap-2 rounded-md border px-3 py-2"
                >
                  <button
                    onClick={() => navigateToScorecard(scorecard.id)}
                    className="hover:bg-accent flex grow items-center gap-2 rounded-md px-2 py-1 text-left transition-colors"
                  >
                    <div className="grow">
                      <h3 className="font-medium">{scorecard.name}</h3>
                      {scorecard.description && (
                        <p className="text-muted-foreground text-sm">
                          {scorecard.description}
                        </p>
                      )}
                    </div>
                    <div className="text-muted-foreground shrink-0">
                      {scorecard.publishedAt ? (
                        <span className="text-success text-xs font-medium">
                          Published
                        </span>
                      ) : (
                        <span className="text-xs">Draft</span>
                      )}
                    </div>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    icon="x"
                    onClick={() => handleRemoveScorecard(scorecard.id)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground rounded-md border px-4 py-8 text-center text-sm">
              No scorecards in this workspace.
            </div>
          )}
        </section>

        <section className="border-t py-6">
          <GoalsForm
            values={workspace.thresholds}
            onSubmit={handleGoalsUpdate}
            placeholders={org?.thresholds}
          />
        </section>

        <AlertDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete workspace"
          description="Are you sure you want to delete this workspace? This will also archive all scorecards in this workspace. This action cannot be undone."
          action={{
            onClick: handleDelete,
            value: 'Delete',
            variant: 'destructive',
          }}
        />
      </div>
    </div>
  )
}
